import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Add, Edit, Delete, ArrowUpward, ArrowDownward } from '@mui/icons-material';

const ADSBChecksManager = ({ checks, onChange, readOnly = false }) => {
  const [localChecks, setLocalChecks] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editFormData, setEditFormData] = useState({
    sequenceLabel: '',
    description: '',
    taskReferenceId: '',
    applicable: true,
    applicabilityRemark: ''
  });

  useEffect(() => {
    if (checks && checks.length > 0) {
      setLocalChecks(checks);
    } else {
      setLocalChecks([]);
    }
  }, [checks]);

  const handleAdd = () => {
    const newCheck = {
      sequenceNumber: localChecks.length + 1,
      sequenceLabel: '',
      description: '',
      taskReferenceId: '',
      applicable: true,
      applicabilityRemark: ''
    };
    const updated = [...localChecks, newCheck];
    setLocalChecks(updated);
    onChange(updated);
    setEditingIndex(updated.length - 1);
    setEditFormData(newCheck);
    setEditDialogOpen(true);
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    const check = localChecks[index];
    setEditFormData({
      sequenceLabel: check.sequenceLabel ?? '',
      description: check.description ?? '',
      taskReferenceId: check.taskReferenceId ?? '',
      applicable: check.applicable !== false,
      applicabilityRemark: check.applicabilityRemark ?? ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (!editFormData.description.trim()) return;
    const updated = [...localChecks];
    updated[editingIndex] = {
      ...updated[editingIndex],
      sequenceLabel: (editFormData.sequenceLabel || '').trim() || undefined,
      description: editFormData.description,
      taskReferenceId: editFormData.taskReferenceId || '',
      applicable: editFormData.applicable,
      applicabilityRemark: editFormData.applicabilityRemark?.trim() || ''
    };
    setLocalChecks(updated);
    onChange(updated);
    setEditDialogOpen(false);
    setEditingIndex(null);
    setEditFormData({ sequenceLabel: '', description: '', taskReferenceId: '', applicable: true, applicabilityRemark: '' });
  };

  const handleDelete = (index) => {
    const updated = localChecks.filter((_, i) => i !== index);
    const reordered = updated.map((c, i) => ({ ...c, sequenceNumber: i + 1 }));
    setLocalChecks(reordered);
    onChange(reordered);
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const updated = [...localChecks];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    const reordered = updated.map((c, i) => ({ ...c, sequenceNumber: i + 1 }));
    setLocalChecks(reordered);
    onChange(reordered);
  };

  const handleMoveDown = (index) => {
    if (index === localChecks.length - 1) return;
    const updated = [...localChecks];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    const reordered = updated.map((c, i) => ({ ...c, sequenceNumber: i + 1 }));
    setLocalChecks(reordered);
    onChange(reordered);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>AD/SB Checks</Typography>
        {!readOnly && (
          <Button variant="outlined" size="small" startIcon={<Add />} onClick={handleAdd}>
            Add Check
          </Button>
        )}
      </Box>
      {localChecks.length === 0 ? (
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">No checks. Click "Add Check" to add one.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="60">S/N</TableCell>
                <TableCell width="80">S/N Label</TableCell>
                <TableCell>Description</TableCell>
                <TableCell width="100">Applicable</TableCell>
                <TableCell>Remark</TableCell>
                {!readOnly && <TableCell width="120">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {localChecks.map((check, index) => (
                <TableRow key={index}>
                  <TableCell>{check.sequenceNumber}</TableCell>
                  <TableCell>{check.sequenceLabel && String(check.sequenceLabel).trim() ? check.sequenceLabel : '—'}</TableCell>
                  <TableCell>{check.description || '(No description)'}</TableCell>
                  <TableCell>{check.applicable !== false ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{check.applicabilityRemark || '—'}</TableCell>
                  {!readOnly && (
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => handleMoveUp(index)} disabled={index === 0}>
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleMoveDown(index)} disabled={index === localChecks.length - 1}>
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleEdit(index)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(index)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingIndex !== null ? 'Edit AD/SB Check' : 'Add AD/SB Check'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Sequence (S/N) Label"
              value={editFormData.sequenceLabel}
              onChange={(e) => setEditFormData({ ...editFormData, sequenceLabel: e.target.value })}
              placeholder="Optional e.g. 1-1, 1-a"
            />
            <TextField
              fullWidth
              label="Description *"
              value={editFormData.description}
              onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
              required
              multiline
              rows={2}
            />
            <TextField
              fullWidth
              label="Task Reference ID"
              value={editFormData.taskReferenceId}
              onChange={(e) => setEditFormData({ ...editFormData, taskReferenceId: e.target.value })}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editFormData.applicable}
                  onChange={(e) => setEditFormData({ ...editFormData, applicable: e.target.checked })}
                />
              }
              label="Applicable"
            />
            <TextField
              fullWidth
              label="Applicability Remark"
              value={editFormData.applicabilityRemark}
              onChange={(e) => setEditFormData({ ...editFormData, applicabilityRemark: e.target.value })}
              placeholder="Remark when not applicable"
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={!editFormData.description?.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ADSBChecksManager;
